import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import FormattedReactMessage from 'I18n/components/FormattedReactMessage';
import { AddIcon } from 'foundations-assets/icons/components/AddIcon';
import { Flex } from 'foundations-components/components/Flex';
import { View } from 'foundations-components/components/View';
import { Alert } from 'foundations-components/transitional/components/Alert';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { DescriptionList } from 'foundations-components/transitional/components/DescriptionList';
import { HelpIcon } from 'foundations-components/transitional/components/HelpIcon';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Link } from 'foundations-components/transitional/components/Link';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Small } from 'foundations-components/transitional/components/Small';
import { Tag } from 'foundations-components/transitional/components/Tag';
import { MarketplaceCardSmall } from 'marketplace-embed/components/MarketplaceCardSmall';
import { useMarketplaceListingModal } from 'marketplace-embed/hooks/useMarketplaceListingModal';
import { useUsageTracker } from 'marketplace-ui-tracking';
import { useCallback, useMemo } from 'react';
import { PlanCompatibilityMessage } from '../../compatibility/PlanCompatibilityMessage';
import { useDetailsPageContext } from 'marketplace-ui-common/context/DetailsPageContext';
import { useFeatureCompatibilities } from '../../hooks/useFeatureCompatibilities';
import { useGetStorefrontListings } from '../../hooks/useGetStorefrontListings';
import { PermissionsMessage } from '../../permissions/PermissionsMessage';
import { openListingModalOrListingInNewPage } from 'marketplace-ui-common/utils/DetailsPageUrls';
import { DetailsPageCard } from './DetailsPageCard';
import { ExternalLinkIcon } from 'foundations-assets/icons/components/ExternalLinkIcon';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export function ProductRequirementsCard({
  installType,
  features,
  builtByHubSpot,
  isUserLevel,
  neededScopeGroups,
  onCompatibilityModalOpen,
  pricingPlans,
  requiredListingIds,
  currentVersion
}) {
  const {
    name,
    productType,
    appContext,
    trackInteraction,
    isEmbedded
  } = useDetailsPageContext();
  const tracker = useUsageTracker();
  const {
    open: openListingDetailsModal
  } = useMarketplaceListingModal({});
  const shouldShowCompatibility = productType === 'APP';
  const {
    listingCards,
    loading: requiredListingDataLoading
  } = useGetStorefrontListings({
    searchParams: {
      searchIds: requiredListingIds || [],
      searchBy: 'LISTING_ID'
    },
    skip: appContext === 'admin-preview' || !requiredListingIds || requiredListingIds.length === 0
  });
  const {
    result: featureCompatibilitiesData,
    loading: featureCompatibilitiesLoading,
    error: featureCompatibilitiesError
  } = useFeatureCompatibilities({
    appContext,
    features,
    productType
  });
  const scrollToPricing = () => {
    const tab = document.querySelector(`#details-page-pricing`);
    if (tab) {
      tab.scrollIntoView({
        behavior: 'smooth'
      });
    }
  };
  const compatibilityContext = appContext === 'app' ? 'app' : 'public';
  const handleTrackInteraction = useCallback(eventProps => {
    trackInteraction(Object.assign({
      section: 'product requirements card'
    }, eventProps));
  }, [trackInteraction]);
  const compatibilityType = useMemo(() => {
    const compatibilities = Object.values(featureCompatibilitiesData);
    if (compatibilities.length === 0) {
      return 'COMPATIBLE_ALL'; // Default to compatible when no features exist
    }
    const allCompatible = compatibilities.every(compatibility => compatibility.satisfied);
    const noneCompatible = compatibilities.every(compatibility => !compatibility.satisfied);
    if (allCompatible) {
      return 'COMPATIBLE_ALL';
    } else if (noneCompatible) {
      return 'COMPATIBLE_NONE';
    } else {
      return 'COMPATIBLE_SOME';
    }
  }, [featureCompatibilitiesData]);
  const renderedPricingMessage = useMemo(() => {
    if (productType === 'APP' && pricingPlans) {
      return /*#__PURE__*/_jsx(Link, {
        onClick: () => {
          handleTrackInteraction({
            action: 'click pricing link'
          });
          scrollToPricing();
        },
        children: /*#__PURE__*/_jsx(Small, {
          children: /*#__PURE__*/_jsx(FormattedReactMessage, {
            message: `detailsPage.requirementsCard.pricing.text.${productType}`,
            options: {
              count: pricingPlans.length,
              plans: /*#__PURE__*/_jsx(FormattedList, {
                exclusive: true,
                children: pricingPlans.map((pricing, index) => /*#__PURE__*/_jsx("span", {
                  children: pricing.pricingName
                }, `pricing-${index}`))
              })
            }
          })
        })
      });
    } else if (productType === 'AGENT') {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: `detailsPage.requirementsCard.pricing.text.${productType}`,
        options: {
          count: 2
        }
      });
    }
    return null;
  }, [handleTrackInteraction, pricingPlans, productType]);
  let renderedPlanCompatibilityMessage;
  if (featureCompatibilitiesLoading) {
    renderedPlanCompatibilityMessage = /*#__PURE__*/_jsx(LoadingSpinner, {
      size: "small"
    });
  } else if (featureCompatibilitiesError) {
    renderedPlanCompatibilityMessage = /*#__PURE__*/_jsx(Alert, {
      type: "danger",
      marginBlock: "200",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "detailsPage.requirementsCard.compatibilityError"
      })
    });
  } else {
    renderedPlanCompatibilityMessage = /*#__PURE__*/_jsx(PlanCompatibilityMessage, {
      appContext: compatibilityContext,
      compatibilityType: compatibilityType,
      helpText: compatibilityType === 'COMPATIBLE_SOME' ? /*#__PURE__*/_jsx(HelpIcon, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "detailsPage.compatibility.disclaimer"
        })
      }) : undefined,
      onCompatibilityModalOpen: () => {
        handleTrackInteraction({
          action: 'open compatibility modal'
        });
        onCompatibilityModalOpen();
      }
    });
  }
  const isRequiredAppClickable = appContext === 'app';
  function handleRequiredAppClicked(slug) {
    if (!isRequiredAppClickable) {
      return null; //should not end up here but adding just in case
    }
    openListingModalOrListingInNewPage(slug, !!isEmbedded, openListingDetailsModal);
  }
  return /*#__PURE__*/_jsx(DetailsPageCard, {
    name: "requirements card",
    title: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "detailsPage.requirementsCard.title"
    }),
    children: /*#__PURE__*/_jsxs(DescriptionList, {
      children: [shouldShowCompatibility && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("dt", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.requirementsCard.hubspotPlan"
          })
        }), /*#__PURE__*/_jsx("dd", {
          children: renderedPlanCompatibilityMessage
        })]
      }), neededScopeGroups && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("dt", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.requirementsCard.permissions.title"
          })
        }), /*#__PURE__*/_jsx("dd", {
          children: /*#__PURE__*/_jsx(PermissionsMessage, {
            installType: installType,
            builtByHubSpot: builtByHubSpot,
            neededScopeGroups: neededScopeGroups,
            onTrackInteraction: handleTrackInteraction
          })
        })]
      }), typeof isUserLevel === 'boolean' && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("dt", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.requirementsCard.accessType.title"
          })
        }), /*#__PURE__*/_jsx("dd", {
          children: /*#__PURE__*/_jsx(Tag, {
            marginBlockStart: "100",
            marginInlineStart: "-50",
            children: isUserLevel ? /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(FormattedMessage, {
                message: "detailsPage.requirementsCard.accessType.personal"
              }), /*#__PURE__*/_jsx(HelpIcon, {
                marginInlineStart: "100",
                title: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "detailsPage.requirementsCard.accessType.personalTooltip"
                }),
                tooltipProps: {
                  onOpenChange: ({
                    target: {
                      value: tooltipOpen
                    }
                  }) => {
                    if (tooltipOpen) {
                      handleTrackInteraction({
                        action: 'open access type tooltip',
                        value: 'personal'
                      });
                    }
                  }
                }
              })]
            }) : /*#__PURE__*/_jsxs(_Fragment, {
              children: [/*#__PURE__*/_jsx(FormattedMessage, {
                message: "detailsPage.requirementsCard.accessType.portal"
              }), /*#__PURE__*/_jsx(HelpIcon, {
                marginInlineStart: "100",
                title: /*#__PURE__*/_jsx(FormattedMessage, {
                  message: "detailsPage.requirementsCard.accessType.portalTooltip"
                }),
                tooltipProps: {
                  onOpenChange: ({
                    target: {
                      value: tooltipOpen
                    }
                  }) => {
                    if (tooltipOpen) {
                      handleTrackInteraction({
                        action: 'open access type tooltip',
                        value: 'portal'
                      });
                    }
                  }
                }
              })]
            })
          })
        })]
      }), pricingPlans && pricingPlans.length > 0 && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("dt", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: `detailsPage.requirementsCard.pricing.title.${productType}`,
            options: {
              name
            }
          })
        }), /*#__PURE__*/_jsx("dd", {
          children: renderedPricingMessage
        })]
      }), requiredListingIds && /*#__PURE__*/_jsx(Flex, {
        gap: "200",
        flexDirection: "column",
        children: !requiredListingDataLoading && listingCards && listingCards.length > 0 ? listingCards.map(listingCard => /*#__PURE__*/_jsx(MarketplaceCardSmall, {
          tracker: tracker,
          listing: listingCard,
          buttons: isRequiredAppClickable ? {
            secondary: [{
              label: isEmbedded ? /*#__PURE__*/_jsx(ExternalLinkIcon, {
                size: "500"
              }) : /*#__PURE__*/_jsx(IconHolder, {
                size: "300",
                borderWidth: "100",
                borderRadius: "300",
                borderColor: "border-core-default",
                color: "text-core-default",
                icon: AddIcon
              }),
              onClick: () => handleRequiredAppClicked(listingCard.slug)
            }]
          } : {}
        }, listingCard.listingId)) : requiredListingIds === null || requiredListingIds === void 0 ? void 0 : requiredListingIds.map(listingId => /*#__PURE__*/_jsx(CardWrapper, {
          compact: true,
          children: /*#__PURE__*/_jsx(View, {
            alignSelf: "center",
            children: /*#__PURE__*/_jsx(LoadingSpinner, {
              use: "primary-white"
            })
          })
        }, listingId))
      }), currentVersion && /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx("dt", {
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "detailsPage.requirementsCard.currentVersion.title"
          })
        }), /*#__PURE__*/_jsx("dd", {
          children: /*#__PURE__*/_jsx(Tag, {
            marginBlockStart: "100",
            marginInlineStart: "-50",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "detailsPage.requirementsCard.currentVersion.label",
              options: {
                version: currentVersion
              }
            })
          })
        })]
      })]
    })
  });
}